// The one place that talks to kinedb.
//
// With no URL, the client finds the server itself: in `bun dev` it sends to
// the same origin and the Vite proxy forwards; under `/ui/` it strips the path
// and uses the origin of the page.
//
// cvr.kinedb.com runs KINEDB_CLIENT_AUTH=required. The site sends NO login, in the
// browser and on the server: it is the guest, which may SELECT the collections that
// have GRANT SELECT … TO ANYONE, and CALL the two queue functions (B09, owner
// 2026-09-27/28; db/access.js).
//
// On the server (the loaders of server.js) there is no page origin, so the URL
// comes from KINEDB_URL.

import { KineDB, defaultBaseUrl } from '@kinedb/client';
import { mapRows } from './rows.js';
import { select, eq, and } from './sql.js';
import { candidateWalk, scanRefusal } from './plan.js';
import { filterQuery, filterWhere } from './filters.js';
import { mergersOf } from './history.js';
import { companyLookups } from './relations.js';
import { REPORT_QUEUE, companyCallSql, companyRequestRowSql, reportsCallSql } from './queue.js';

const serverEnv = typeof window === 'undefined' ? globalThis.process?.env ?? {} : null;
const baseUrl = serverEnv?.KINEDB_URL ?? defaultBaseUrl();

// A `retry: true` refusal means the request did not apply, and the client can
// send it again without loss (kinedb B725 / B649).
const client = new KineDB(baseUrl || undefined, undefined, {
  retry: { maxRetries: 30, baseDelayMs: 5, maxDelayMs: 500 },
});

async function send(sql) {
  return client.sql(sql);
}

// A large OFFSET scans and drops each row before it. An `OFFSET 500000` on this
// node came just before an OOM kill (cvrweb CLAUDE.md, "Do not"). Page with a
// search or a sort instead.
export const MAX_OFFSET = 10000;

// A relation list with more rows than this is cut, and the page says so.
export const RELATION_LIMIT = 1000;

async function rows(sql) {
  return mapRows(await send(sql));
}

async function planOf(sql) {
  return rows(`EXPLAIN ${sql}`);
}

const sortPlans = new Map();

/**
 * True when kinedb serves this unfiltered sort from an ordered index.
 * Without one, `ORDER BY ... LIMIT` sorts the whole collection: 495 s on cvr
 * (cvrweb CLAUDE.md, item 5). The answer is kept for each collection and field.
 * A failed EXPLAIN answers false and is not kept.
 */
export async function sortIsServed(from, order) {
  if (!order?.field) return true;
  const key = `${from}|${order.field}|${order.desc ? 'desc' : 'asc'}`;
  if (sortPlans.has(key)) return sortPlans.get(key);
  try {
    const steps = await planOf(select({ from, fields: ['_id'], order, limit: 1 }));
    const served = steps.some((step) => step.operation === 'index-ordered scan');
    sortPlans.set(key, served);
    return served;
  } catch {
    return false;
  }
}

/** Refuse a filtered query that no index serves. See plan.js for the rules. */
async function refuseScan(sql) {
  const reason = scanRefusal(await planOf(sql));
  if (reason) throw new Error(reason);
}

/**
 * One page of a list.
 * `plan` comes from planSearch, `filters` from the checkboxes (filters.js). The answer
 * has `hasNext` because the page asks for one row more than it shows; a COUNT of the
 * whole collection is not needed.
 */
export async function listPage({ from, fields, plan, filters, order, page, pageSize }) {
  const offset = (page - 1) * pageSize;
  if (offset > MAX_OFFSET) {
    throw new Error(`The site does not page past row ${MAX_OFFSET}. Search, or change the sort.`);
  }
  const query = filterQuery({
    where: plan.kind === 'where' || plan.kind === 'text' ? plan.where : '',
    filters,
    order,
    offset,
  });
  const { where, trusted } = query;

  let sortApplied = Boolean(order?.field);
  if (trusted) sortApplied = Boolean(query.order);
  else if (sortApplied && !where) sortApplied = await sortIsServed(from, order);
  let effectiveOrder = sortApplied ? order : undefined;

  // A sort walk over the candidates of a search can run for minutes (plan.js).
  if (where && effectiveOrder && !trusted) {
    const sorted = select({ from, fields, where, order: effectiveOrder, limit: pageSize + 1, offset });
    if (candidateWalk(await planOf(sorted))) {
      sortApplied = false;
      effectiveOrder = undefined;
    }
  }

  const sql = select({ from, fields, where, order: effectiveOrder, limit: pageSize + 1, offset });
  if (where && !trusted) await refuseScan(sql);
  let items = await rows(sql);

  // On page 1 of a name search, the company with exactly that name goes first.
  if (plan.kind === 'text' && page === 1) {
    const exact = await rows(select({ from, fields, where: and(plan.exact, filterWhere(filters)), limit: 1 }));
    if (exact.length > 0) items = [exact[0], ...items.filter((item) => item._id !== exact[0]._id)];
  }

  return {
    items: items.slice(0, pageSize),
    hasNext: items.length > pageSize,
    sortApplied,
    filtered: query.filtered,
    sql,
  };
}

/** The company of a URL id: a CVR number or an enhedsnummer (see companyLookups). Index-served. */
export async function findCompany(id) {
  for (const [field, number] of companyLookups(id)) {
    const found = await rows(select({ from: 'cvr', where: eq(field, number), limit: 1 }));
    if (found.length > 0) return found[0];
  }
  return null;
}

export async function findPerson(id) {
  const number = Number(id);
  if (!Number.isSafeInteger(number)) return null;
  const found = await rows(select({ from: 'cvrperson', where: eq('enhedsNummer', number), limit: 1 }));
  return found[0] ?? null;
}

/** The enhedsNummer of a CVR number, or null. */
export async function enhedsNummerOfCvr(cvr) {
  const found = await rows(select({ from: 'cvr', fields: ['enhedsNummer'], where: eq('cvr', cvr), limit: 1 }));
  return found[0]?.enhedsNummer ?? null;
}

/** The companies in which `enhedsNummer` is a participant. Index-served. */
export async function companiesWithParticipant(enhedsNummer) {
  return rows(select({
    from: 'cvr',
    fields: ['enhedsNummer', 'cvr', 'name', 'navne', 'cvrstatus', 'deltagerRelation'],
    where: and(eq('deltagerRelation.deltager.enhedsNummer', enhedsNummer)),
    limit: RELATION_LIMIT,
  }));
}

const MERGER_FIELDS = ['enhedsNummer', 'cvr', 'name', 'navne', 'fusioner'];
// A merger with more companies than this is cut.
const MERGER_LIMIT = 100;

/**
 * The other companies in the mergers of `company`: one query for each merger unit,
 * served by idx_cvr_fusioner_enhedsnummerorganisation (created on cvr.kinedb.com
 * 2026-09-17). None when the company has no merger.
 */
export async function mergerPartners(company) {
  const found = [];
  for (const unit of new Set(mergersOf(company).map((merger) => merger.unit))) {
    // A node without the index would scan the whole of cvr.
    const sql = select({ from: 'cvr', fields: MERGER_FIELDS, where: eq('fusioner.enhedsNummerOrganisation', unit), limit: MERGER_LIMIT });
    await refuseScan(sql);
    found.push(...await rows(sql));
  }
  return found;
}

// THE QUEUES (owner, 2026-09-15 and 2026-09-26). A button CALLs a queue function as the
// guest; the function runs as web and writes the row (queue.js, db/access.js). It answers
// {queued: true}, or {error} when it refused an argument.
async function callQueue(sql) {
  const answer = await send(sql);
  const value = answer?.data?.[0]?.[0];
  if (value?.error) throw new Error(value.error);
  if (!value?.queued) throw new Error('The queue did not answer as expected.');
}

/** Ask the agent for the reports of a company. One row for each company. */
export async function requestReports(company) {
  await callQueue(reportsCallSql({ enhedsNummer: Number(company.enhedsNummer), cvr: Number(company.cvr), name: company.name ?? '' }));
}

/** The queue row of a company `{attempts}`, or null when no request is waiting. */
export async function reportRequest(enhedsNummer) {
  const found = await rows(select({ from: REPORT_QUEUE, fields: ['attempts'], where: eq('enhedsNummer', Number(enhedsNummer)), limit: 1 }));
  return found[0] ?? null;
}

/** Ask the agent for a company that kinedb does not have (see queue.js). */
export async function requestCompany(cvr) {
  await callQueue(companyCallSql(cvr));
}

/** The queue row of a CVR number `{attempts, lastError}`, or null when no request is waiting. */
export async function companyRequest(cvr) {
  const found = await rows(companyRequestRowSql(cvr));
  return found[0] ?? null;
}

/**
 * The XBRL of one annual report `{start, xml}`, or null. Served by the (enhedsNummer,
 * start) index. One report is about 20 KB; the page asks only for the fiscal year of a
 * capital increase. A nested select such as `xml.xbrli:xbrl` is no identifier in
 * kinedb SQL, so the whole `xml` comes back.
 */
export async function financialXml(enhedsNummer, start) {
  const found = await rows(select({
    from: 'cvrfinancial',
    fields: ['start', 'xml'],
    where: and(eq('enhedsNummer', Number(enhedsNummer)), eq('start', String(start))),
    limit: 1,
  }));
  return found[0] ?? null;
}

/** The newest 12 annual reports of a company. Served by the (enhedsNummer, start) index. */
export async function financialsOf(enhedsNummer) {
  return rows(select({
    from: 'cvrfinancial',
    fields: ['start', 'startDate', 'endDate', 'report'],
    where: eq('enhedsNummer', enhedsNummer),
    order: { field: 'startDate', desc: true },
    limit: 12,
  }));
}
